'use client'

import * as React from 'react'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import { cn } from '@/lib/utils'
import { todayIST, isWeekendYMD } from '@/lib/date-utils'

export interface MonthGridDayMeta {
  /** Day of month, 1-based. */
  day: number
  isWeekend: boolean
  isToday: boolean
  /** Later than today (IST) — nothing has happened on it yet. */
  isFuture: boolean
}

export interface MonthGridCell {
  /** Classes for the cell body — use it to carry the day's colour/tone. */
  className?: string
  content?: React.ReactNode
  title?: string
  disabled?: boolean
}

interface MonthGridProps {
  /** 'YYYY-MM'. */
  month: string
  onMonthChange?: (month: string) => void
  renderDay: (date: string, meta: MonthGridDayMeta) => MonthGridCell
  onDayClick?: (date: string) => void
  /** Rendered to the right of the month name — typically a legend. */
  toolbar?: React.ReactNode
  hint?: string
  className?: string
}

const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']

export function shiftMonth(month: string, delta: number): string {
  const [y, m] = month.split('-').map(Number)
  const d = new Date(y, m - 1 + delta, 1)
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`
}

export function monthLabel(month: string): string {
  const [y, m] = month.split('-').map(Number)
  return new Date(y, m - 1, 1).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })
}

/**
 * Bare Monday-first month grid. AttendanceCalendar owns the per-employee
 * attendance semantics; this one leaves every cell's meaning to the caller, so
 * company-wide views (holidays, headcount per day) can share the same chrome.
 */
export function MonthGrid({ month, onMonthChange, renderDay, onDayClick, toolbar, hint, className }: MonthGridProps) {
  const today = todayIST()
  const [year, mon] = month.split('-').map(Number)

  const cells = React.useMemo(() => {
    const first = new Date(year, mon - 1, 1)
    // Grid is Monday-first; getDay() is Sunday-first, so rotate by 6.
    const lead = (first.getDay() + 6) % 7
    const daysInMonth = new Date(year, mon, 0).getDate()
    const out: ({ date: string; meta: MonthGridDayMeta } | null)[] = []
    for (let i = 0; i < lead; i++) out.push(null)
    for (let d = 1; d <= daysInMonth; d++) {
      const date = `${month}-${String(d).padStart(2, '0')}`
      out.push({
        date,
        meta: { day: d, isWeekend: isWeekendYMD(date), isToday: date === today, isFuture: date > today },
      })
    }
    while (out.length % 7 !== 0) out.push(null)
    return out
  }, [month, year, mon, today])

  return (
    <div className={cn('space-y-3', className)}>
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-1">
          {onMonthChange && (
            <button
              type="button"
              aria-label="Previous month"
              onClick={() => onMonthChange(shiftMonth(month, -1))}
              className="rounded-lg p-1.5 text-neutral-500 hover:bg-neutral-100 hover:text-neutral-900 dark:text-white/50 dark:hover:bg-white/10 dark:hover:text-white transition-colors"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
          )}
          <h3 className="min-w-[9.5rem] text-center text-sm font-semibold tracking-tight text-neutral-900 dark:text-white/90 sm:text-left">
            {monthLabel(month)}
          </h3>
          {onMonthChange && (
            <button
              type="button"
              aria-label="Next month"
              onClick={() => onMonthChange(shiftMonth(month, 1))}
              className="rounded-lg p-1.5 text-neutral-500 hover:bg-neutral-100 hover:text-neutral-900 dark:text-white/50 dark:hover:bg-white/10 dark:hover:text-white transition-colors"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          )}
        </div>
        {toolbar}
      </div>

      {hint && <p className="text-[11px] text-neutral-400 dark:text-white/40">{hint}</p>}

      <div className="grid grid-cols-7 gap-1 sm:gap-1.5">
        {WEEKDAYS.map(w => (
          <div
            key={w}
            className="pb-1 text-center text-[10px] font-medium uppercase tracking-wider text-neutral-400 dark:text-white/40"
          >
            <span className="hidden sm:inline">{w}</span>
            <span className="sm:hidden">{w[0]}</span>
          </div>
        ))}

        {cells.map((c, i) => {
          if (!c) return <div key={`pad-${i}`} />
          const cell = renderDay(c.date, c.meta)
          const clickable = !!onDayClick && !cell.disabled
          return (
            <button
              key={c.date}
              type="button"
              title={cell.title}
              disabled={!clickable}
              onClick={clickable ? () => onDayClick!(c.date) : undefined}
              className={cn(
                'relative flex aspect-square flex-col items-center justify-center gap-0.5 rounded-xl p-1 ring-1 ring-inset transition-all',
                cell.className,
                clickable && 'cursor-pointer hover:-translate-y-0.5 motion-reduce:hover:translate-y-0',
                !clickable && 'cursor-default',
                c.meta.isToday && 'ring-2 ring-orange-500 dark:ring-orange-400'
              )}
            >
              <span
                className={cn(
                  'absolute left-1.5 top-1 text-[10px] font-medium tabular-nums',
                  c.meta.isToday ? 'text-orange-600 dark:text-orange-300' : 'text-neutral-400 dark:text-white/40'
                )}
              >
                {c.meta.day}
              </span>
              {cell.content}
            </button>
          )
        })}
      </div>
    </div>
  )
}
